import React from 'react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import { useStaticQuery, graphql } from 'gatsby'

const SEO = ({
  content,
  full_slug,
  name,
  title,
  description,
  og_title,
  og_description,
  og_type,
  og_url,
  og_image,
  og_image_width,
  og_image_height,
  article_published_time,
  article_modified_time,
  og_locale,
  twitter_title,
  twitter_description,
  twitter_image,
}) => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            titleSeparator
            description
            author
            twitter_creator
            siteUrl
            color
          }
        }
      }
    `
  )

  const og_site_name = site.siteMetadata?.title
  const twitter_creator = site.siteMetadata?.twitter_creator

  const bestMatch = (array) => {
    return array.find((e) => e !== undefined && e !== '')
  }

  const c =
    content && Object.keys(content).length > 0 ? content : { meta_seo: {} }
  const m = c.metadata ? c.metadata : {}
  //const body = content?.body ? content.body : null

  /*   const getFirstItem = (type) => {
    let arr = []
    body &&
      body !== {} &&
      body?.forEach((items) => {
        for (const [key, value] of Object.entries(items)) {
          key === type && arr.push(value)
        }
      })

    return arr[0]
  } */

  const IMAGE_SIZE = {
    facebook: {
      width: '1200',
      height: '630',
    },
    twitter: {
      width: '1024',
      height: '512',
    },
  }

  const resizeImage = (image, sizes) => {
    let fileName
    let imageSize
    let imageUrl = image && typeof image === String ? image : image?.filename
    const imageService = 'https://img2.storyblok.com'
    const sbimage =
      image && typeof image === String
        ? /^(?=.*\bstoryblok\b).*$/.test(image)
        : /^(?=.*\bstoryblok\b).*$/.test(image?.filename)
    if (sbimage) {
      if (typeof image === String) {
        fileName = image?.replace('//a.storyblok.com/', '')
      } else {
        fileName = image?.filename.replace('//a.storyblok.com/', '')
      }
      imageSize = `${sizes.width}x${sizes.height}`
      imageUrl = `${imageService}/${imageSize}/smart/${fileName}`
    }
    return imageUrl
  }

  let data = []
  const { firstSegment, secondSegment } = (() => {
    switch (content?.component) {
      case 'product_page':
        return {
          firstSegment: `${name} - ${c.main_category?.title}`,
          secondSegment: `${site.siteMetadata?.titleSeparator}${site.siteMetadata?.title}`,
        }
      case 'article_page':
        return {
          firstSegment: `${name} - ${c.headline}`,
          secondSegment: `${site.siteMetadata?.titleSeparator}${site.siteMetadata?.title}`,
        }
      case 'home_page':
        return {
          firstSegment: bestMatch([m.title, title, c.headline, c.title, name]),
          secondSegment: '',
        }
      default:
        return {
          firstSegment: bestMatch([m.title, title, c.headline, c.title, name]),
          secondSegment: `${site.siteMetadata?.titleSeparator}${site.siteMetadata?.title}`,
        }
    }
  })()

  const fullTitle = `${firstSegment}${secondSegment}`
  // SEO Variablen (einige sind optional)
  data.push({
    name: `description`,
    content: bestMatch([m.description, description, c.subheadline, c.teaser]),
  })
  data.push({
    property: `msapplication-TileColor`,
    content: site.siteMetadata?.color,
  })
  data.push({
    property: `og:site_name`,
    content: og_site_name,
  })
  data.push({
    property: `og:title`,
    content: bestMatch([m.og_title, og_title, fullTitle]),
  })
  data.push({
    property: `og:description`,
    content: bestMatch([
      m.og_description,
      og_description,
      m.description,
      description,
      c.subheadline,
      c.teaser,
    ]),
  })
  data.push({
    property: `og:type`,
    content: og_type ? og_type : 'website',
  })
  data.push({
    property: `og:url`,
    content: bestMatch([og_url, full_slug]),
  })
  data.push({
    property: `og:image`,
    content: resizeImage(
      bestMatch([
        og_image,
        m.og_image,
        c.header_image,
        c.main_image,
        c.isolated_image,
      ]),
      IMAGE_SIZE.facebook
    ),
  })
  data.push({
    property: `og:image:width`,
    content: og_image_width,
  })
  data.push({
    property: `og:image:height`,
    content: og_image_height,
  })
  data.push({
    property: `article:published_time`,
    content: article_published_time,
  })
  data.push({
    property: `article:modified_time`,
    content: article_modified_time,
  })
  data.push({
    property: `og:locale`,
    content: bestMatch([og_locale, c.locale]),
  })
  data.push({
    name: `twitter:card`,
    content: `summary_large_image`,
  })
  data.push({
    name: `twitter:title`,
    content: bestMatch([
      m.twitter_title,
      m.og_title,
      twitter_title,
      og_title,
      fullTitle,
    ]),
  })
  data.push({
    name: `twitter:description`,
    content: bestMatch([
      m.twitter_description,
      m.og_description,
      twitter_description,
      og_description,
      m.description,
      description,
      c.subheadline,
      c.teaser,
    ]),
  })
  data.push({
    name: `twitter:site`,
    content: twitter_creator,
  })
  data.push({
    name: `twitter:creator`,
    content: twitter_creator,
  })
  data.push({
    name: `twitter:image`,
    content: resizeImage(
      bestMatch([
        twitter_image,
        m.twitter_image,
        m.og_image,
        og_image,
        c.header_image,
        c.main_image,
        c.isolated_image,
      ]),
      IMAGE_SIZE.twitter
    ),
  })

  data.push({
    name: `viewport`,
    content:
      'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0',
  })

  data = data.filter((x) => x.content && x.content !== undefined)
  return <Helmet title={fullTitle} meta={data.concat()} />
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string,
}

SEO.defaultProps = {
  og_image_width: '1200',
  og_image_height: '630',
  og_locale: 'de',
}

export default SEO
